'use client'

import { useDeleteBrandMutation } from '@/api/brands/mutations/delete-brand-mutation'
import { IBrand } from '@/api/brands/queries/get-brand-query'
import { useGetBrandsQuery } from '@/api/brands/queries/get-brands-query'
import { useBrandContext } from '@/features/brands/brand-context-definition'
import { buildBrandSegment } from '@/features/brands/brand-url-identifier'
import { toast } from '@/features/toast/toast'
import { UrlConfig } from '@/setup/routing/routing'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'

/**
 * Owns the self-delete flow both the sidebar switcher and the admin table
 * fire: run the mutation, then — only on success — move to the next available
 * brand if the deleted one was active. Navigation is post-success (not
 * pre-mutation) so a failed delete doesn't yank the user off their page.
 *
 * `router.replace` is used directly rather than `BrandContext.setBrand`
 * because `setBrand` silently no-ops from surfaces where its own
 * `useParams().brandSlug` is stale (the sidebar sits above `[brandSlug]`).
 * See `project_brand_switcher_setbrand_pitfall` in memory.
 */
export function useDeleteBrandFlow() {
    const { brand: activeBrand } = useBrandContext()
    const { data: brands } = useGetBrandsQuery()
    const router = useRouter()
    const { mutateAsync: deleteBrand, isPending: isDeleting } = useDeleteBrandMutation()

    const deleteBrandFlow = useCallback(
        async (brandToDelete: IBrand) => {
            const isDeletingActiveBrand =
                !!activeBrand?.uuid && brandToDelete.uuid.toLowerCase() === activeBrand.uuid.toLowerCase()
            const nextBrand = isDeletingActiveBrand
                ? (brands ?? []).find((b) => b.uuid.toLowerCase() !== brandToDelete.uuid.toLowerCase())
                : null

            // Defensive: callers already enforce the "at least one brand"
            // invariant, so this only trips if state raced. Bail before firing
            // the DELETE — nothing to navigate to.
            if (isDeletingActiveBrand && !nextBrand) return

            const nextUrl = nextBrand
                ? UrlConfig['/brands/[brandSlug]'].getUrl({ brandSlug: buildBrandSegment(nextBrand) })
                : null

            try {
                await deleteBrand({ brandSlug: brandToDelete.slug })
                toast.success(`Deleted "${brandToDelete.name}"`)
                if (nextUrl) router.replace(nextUrl)
            } catch (error) {
                toast.error(error instanceof Error ? error.message : 'Failed to delete brand')
                throw error
            }
        },
        [activeBrand, brands, router, deleteBrand]
    )

    return { deleteBrandFlow, isDeleting }
}
